import { useEffect, useSyncExternalStore } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { lmsAdmin } from './api'

// Whether the current organization has the LMS feature (organizations.
// feature_lms), for the sidebar's two LMS entries (Analytics > LMS and
// Integrations > LMS). Staff hold every organization permission, so the
// role gates never said no; the sign-in token carries no LMS feature claim;
// the LMS service is the one that knows. Its admin routes answer
// FEATURE_DISABLED for an organization without the feature (the same error
// the LMS pages show as their "not enabled" state), so one cheap read of
// the connections list is the question.
//
//   FEATURE_DISABLED   no LMS: the entries stay out.
//   an answer          the LMS is on: the entries show.
//   any other failure  (a timeout, a 5xx, the network) is not an answer. A
//                      temporary LMS outage must not take the menu from the
//                      organization that uses it, so the entries show, and
//                      the read is made again at the next mount.
//   still pending      the entries stay out, so a disabled organization's
//                      menu never flickers them into view.
//
// Asked once per organization for the page's life, shared by every caller
// (the sidebar's two copies, the desktop column and the phone drawer), never
// on a render or a route change. `enabled` is the caller's own gate: only a
// session that could see an LMS entry at all asks (lmsAdminVisible and a
// role that reads it), so nobody else's session calls the LMS.

const answers = new Map() // organization id -> true (has the LMS) | false (FEATURE_DISABLED)
const unreadable = new Set() // organization ids whose last read failed some other way
const asking = new Set() // organization ids with a read in flight
const listeners = new Set()
let generation = 0

const notify = () => listeners.forEach(listener => listener())

const subscribe = listener => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// Tests start each case from a fresh page.
export const forgetLmsEnabled = () => {
  generation += 1
  answers.clear()
  unreadable.clear()
  asking.clear()
}

const read = key => {
  if (answers.has(key) || asking.has(key)) return
  const mine = generation
  asking.add(key)
  Promise.resolve()
    .then(() => lmsAdmin.listConnections())
    .then(
      () => {
        if (mine !== generation) return
        answers.set(key, true)
        unreadable.delete(key)
      },
      error => {
        if (mine !== generation) return
        if (error && error.code === 'FEATURE_DISABLED') {
          answers.set(key, false)
          unreadable.delete(key)
        } else {
          unreadable.add(key)
        }
      }
    )
    .finally(() => {
      if (mine !== generation) return
      asking.delete(key)
      notify()
    })
}

const keyOf = state => {
  if (!state) return null
  const claims = state.jwtClaims || {}
  const organization = state.organizationID || claims.organizationID
  return organization ? String(organization) : null
}

// What the entries may do for this organization right now.
const shownFor = key => {
  if (!key) return false
  if (answers.has(key)) return answers.get(key)
  return unreadable.has(key)
}

export const useLmsEnabled = enabled => {
  const { state } = useAuthContext()
  const key = enabled ? keyOf(state) : null

  useEffect(() => {
    if (key) read(key)
  }, [key])

  return useSyncExternalStore(subscribe, () => shownFor(key))
}
